import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useObservable } from 'react-use-observable';
import RxOp from 'rxjs-operators';
import snapshotService, { ISnapshot } from 'services/snapshot';

type SnapshotsValue = {
  snapshots: ISnapshot[] | null;
  selectedSnap: ISnapshot | null;
  selectedSnapId: number | null;
  clearSelectedSnap: () => void;
  changeSelectedSnap: (snapshotId: number) => void;
  redirectWithSnap: (path: string) => void;
  getUrlWithSnap: (path: string) => string;
};

const SnapshotsContext = createContext<SnapshotsValue>({
  snapshots: null,
  selectedSnap: null,
  selectedSnapId: null,
  clearSelectedSnap: () => null,
  changeSelectedSnap: (snapshotId: number) => null,
  redirectWithSnap: (path: string) => null,
  getUrlWithSnap: (path: string) => path,
});

const SnapshotsProvider = ({ children }: PropsWithChildren<{}>) => {
  const [selectedSnap, setSelectedSnap] = useState<ISnapshot | null>(null);
  const { search, pathname } = useLocation();
  const history = useHistory();
  const snapshotIdQuery = useMemo(() => {
    const snapshotId = new URLSearchParams(search).get('snapshotId');
    return snapshotId ? Number(snapshotId) : null;
  }, [search]);

  const selectedSnapId: number | null = useMemo(
    () => (selectedSnap ? selectedSnap.id : null),
    [selectedSnap]
  );

  const [snapshots] = useObservable<ISnapshot[]>(() => {
    return snapshotService.list().pipe(
      RxOp.logError(),
      RxOp.map((snapshots) => snapshots),
      RxOp.tap((results) => results)
    );
  }, []);

  const getUrlWithSnap = useCallback(
    (path: string, snapshotId?: number) => {
      let query = '';
      if (snapshotId) query += '?snapshotId=' + snapshotId;
      else if (selectedSnap) query += `?snapshotId=${selectedSnap.id}`;
      return path + query;
    },
    [selectedSnap]
  );

  const redirectWithSnap = useCallback(
    (path: string, snapshotId?: number) => {
      const _path = getUrlWithSnap(path, snapshotId);
      history.replace(_path);
    },
    [getUrlWithSnap, history]
  );

  useEffect(() => {
    if (snapshots && snapshotIdQuery === 0) {
      const last = snapshots[snapshots.length - 1];
      if (last) {
        redirectWithSnap(pathname, last.id);
      }
    }
  }, [snapshotIdQuery, snapshots, pathname, redirectWithSnap]);

  const changeSelectedSnap = useCallback(
    (snapshotId: number) => {
      if (snapshotId) {
        const snap = snapshots.find((item) => item.id === snapshotId);
        if (snap) {
          redirectWithSnap(pathname, snap.id);
        } else history.replace(pathname);
      } else {
        history.replace(pathname);
      }
    },
    [history, pathname, redirectWithSnap, snapshots]
  );

  useEffect(() => {
    if (!snapshotIdQuery) {
      setSelectedSnap(null);
    } else if (snapshots) {
      const snap = snapshots.find((item) => item.id === snapshotIdQuery);
      if (snap) setSelectedSnap(snap);
    }
  }, [history, pathname, snapshotIdQuery, snapshots]);

  const clearSelectedSnap = () => {
    setSelectedSnap(null);
    history.replace(pathname);
  };

  return (
    <SnapshotsContext.Provider
      value={{
        snapshots,
        selectedSnap,
        redirectWithSnap,
        getUrlWithSnap,
        changeSelectedSnap,
        selectedSnapId,
        clearSelectedSnap,
      }}
    >
      {children}
    </SnapshotsContext.Provider>
  );
};

const useSnapshots = () => useContext(SnapshotsContext);

export { SnapshotsProvider, useSnapshots };
